import {useRef, useEffect, useCallback} from 'react'
import axios, {CancelTokenSource} from 'axios'

const useCancelToken = () => {
  const axiosSource = useRef<CancelTokenSource>(axios.CancelToken.source())

  const newCancelToken = useCallback(() => {
    return axiosSource.current.token
  }, [])

  /**
   * Cancel the previous Axios request (if any) and create a new CancelToken.
   *
   * Ensure that only the latest request is active, cancelling any previous pending requests.
   * It returns the new CancelToken to be used in an Axios request configuration.
   *
   * @returns {import('axios').CancelToken} A new Axios CancelToken for the next request.
   *
   * @example
   * // Usage in an Axios request
   * axios.get('/api/data', {
   *   cancelToken: cancelAndCreateNewToken()
   * });
   *
   * Previous request will be cancelled automatically if it was still pending.
   */
  const cancelAndCreateNewToken = useCallback(() => {
    if (axiosSource.current) {
      axiosSource.current.cancel()
    }

    axiosSource.current = axios.CancelToken.source()
    return axiosSource.current.token
  }, [])

  /**
   * Manually cancel current request
   */
  const cancelToken = useCallback(() => {
    if (axiosSource.current) {
      axiosSource.current.cancel('Canceled manually')
      // Create new token to avoid using a cancelled one
      axiosSource.current = axios.CancelToken.source()
    }
  }, [])

  useEffect(
    () => () => {
      axiosSource.current.cancel()
    },
    []
  )

  return {newCancelToken, isCancel: axios.isCancel, cancelAndCreateNewToken, cancelToken}
}

export default useCancelToken
